import React, { useEffect } from "react";
import PropTypes from "prop-types";
import "./labels-picker.scss";
import { useList } from "react-use";
import { TiWarningOutline } from "react-icons/ti";
import LabelSingleton from "../LabelSingleton/LabelSingleton";
import t from "../../../../utils/translation/translator";

/**
 * Widget for selecting some labels inside of a predifined list of labels.
 * @author Fanny Graille
 */
const LabelsPicker = ({
  label,
  largeLabel,
  labelPosition,
  unit,
  required,
  error,
  recommended,
  options,
  selectedLabelsInit,
  disabledLabels,
  onChange,
  changeWithInit,
  cols,
  notWorking,
  singleChoice,
  large,
  onSelectManually,
  idLabel,
  ...props
}) => {
  const [selectedLabels, { set, push, removeAt }] = useList(selectedLabelsInit);
  useEffect(() => {
    onChange(selectedLabels);
  }, [selectedLabels]);

  useEffect(() => {
    // console.log(label, selectedLabelsInit, changeWithInit);
    // if changeWithInit and selectedLabels is different from selectedLabelsInit
    if (changeWithInit && JSON.stringify(selectedLabels) !== JSON.stringify(selectedLabelsInit)) {
      console.log("set selectedLabels for", label, ":", selectedLabelsInit);
      set(selectedLabelsInit);
    }
  }, [selectedLabelsInit]);
  return (
    <div className={`labels-picker-layout${labelPosition === "top" ? " top" : ""}`} {...props}>
      <span className="sm:flex items-end gap-1 w-full">
        {notWorking && <TiWarningOutline className="text-lg cursor-pointer text-orange-400" title="Not working" />}
        <div className="m-1 md:m-0 space-x-1 ">
          <span
            className={`md:text-xs ${large && " font-semibold text-gray-600"}
            ${largeLabel && "text-sm md:text-sm"}`}
          >
            {label}
          </span>
          {required && <span className="text-red-600 md:text-xs">*</span>}
          {unit && <span className={`text-gray-300 md:text-xs ${large && "text-sm md:text-sm"}`}>({unit})</span>}
          {recommended && <span className="text-gray-300 md:text-xs">({t("msizer.ui.recommended")})</span>}
        </div>

        {error && <span className="text-red-500 text-xxs mb-0.5">{t("msizer.ui.mandatory_field_missing")}</span>}
      </span>
      <div
        className={`-mb-2 md:flex md:flex-wrap w-full ${cols && `!grid grid-cols-2 md:grid-cols-${cols}`}
        ${notWorking && "blur-xs hover:blur-none transition-all duration-100"}
        `}
      >
        {options.map((option) => (
          <LabelSingleton
            idLabel={`filter-${idLabel}`}
            key={option.label}
            label={option.label}
            selectedInit={selectedLabels.includes(option.key)}
            disabled={disabledLabels.includes(option.key)}
            onSelect={(selected) => {
              if (selected) {
                if (singleChoice) {
                  set([]);
                }
                push(option.key);
                onSelectManually();
              } else {
                removeAt(selectedLabels.indexOf(option.key));
              }
            }}
            // changeWithInit={changeWithInit}
            changeWithInit
          />
        ))}
      </div>
    </div>
  );
};

LabelsPicker.propTypes = {
  /**
   * Label of the whole widget
   */
  label: PropTypes.node,
  recommended: PropTypes.bool,
  /**
   * Where the label is placed: on top of the widget, at its left, or nowhere.
   */
  unit: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  labelPosition: PropTypes.oneOf(["top", "left", "none"]),
  largeLabel: PropTypes.bool,
  /**
   * Labels that can be picked. They should contain a label and a key
   * (used to differentiate the labels).
   */
  options: PropTypes.arrayOf(
    PropTypes.exact({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  /**
   * Selected labels' keys at the beginning of the display.
   */
  selectedLabelsInit: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /**
   * Disabled labels' keys.
   */
  disabledLabels: PropTypes.arrayOf(PropTypes.string),
  /**
   * Function called when the selected elements change.
   * @param {arrayOf(string)} selectedLabels selected labels.
   */
  onChange: PropTypes.func,
  changeWithInit: PropTypes.bool,
  cols: PropTypes.number,
  notWorking: PropTypes.bool,
  singleChoice: PropTypes.bool,
  large: PropTypes.bool,
  onSelectManually: PropTypes.func,
  idLabel: PropTypes.string
};

LabelsPicker.defaultProps = {
  label: "",
  labelPosition: "top",
  largeLabel: false,
  unit: null,
  required: false,
  error: false,
  recommended: false,
  options: [],
  selectedLabelsInit: [],
  disabledLabels: [],
  onChange: () => {},
  changeWithInit: false,
  cols: null,
  notWorking: false,
  singleChoice: false,
  large: false,
  onSelectManually: () => {},
  idLabel: "default"
};

export default LabelsPicker;
